import { Link } from '@tanstack/react-router';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useSupplier } from '@/api/rpc/hooks/accounts-payable/useSupplier';
import { useSupplierPaymentDetails } from '@/api/rpc/hooks/accounts-payable/useSupplierPaymentDetails';
import { useUpdatePayment } from '@/api/rpc/hooks/finance/useUpdatePayment';
import { Skeleton } from '@/components/ui/skeleton';
import { T, useGT } from 'gt-react';
import { WarningIcon } from '@phosphor-icons/react';
import type { SupplierPaymentDetails } from '@erp/rpc';

interface SupplierBankSelectorProps {
  paymentId: string;
  supplierId: string;
  currentDetailsId: string | null | undefined;
  /** When false, renders a read-only label instead of the select. */
  editable: boolean;
  /**
   * Show the red "no bank available" warning when neither a selected bank nor
   * a supplier default can resolve. Pass for payments that have not executed
   * yet (in_review) — executed payments carry their own snapshot.
   */
  warnIfMissing: boolean;
}

const AUTO_VALUE = '__auto__';

function formatBankLabel(details: SupplierPaymentDetails): string {
  const name = details.label || details.bankName || details.method;
  return details.accountNumberMasked
    ? `${name} (${details.accountNumberMasked})`
    : name;
}

export function SupplierBankSelector({
  paymentId,
  supplierId,
  currentDetailsId,
  editable,
  warnIfMissing,
}: SupplierBankSelectorProps) {
  const gt = useGT();
  const { paymentDetails, isLoading } = useSupplierPaymentDetails(supplierId);
  const updatePayment = useUpdatePayment();
  const { data: supplier } = useSupplier(
    warnIfMissing ? supplierId : undefined,
  );

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  const currentDetails = currentDetailsId
    ? paymentDetails.find((d) => d.id === currentDetailsId)
    : undefined;

  // No selected bank (or it was deleted) and no supplier default to fall back
  // to — approval would fail. Surface it prominently with a path to fix it.
  const hasDefault = paymentDetails.some((d) => d.isDefault);
  const missingBankWarning =
    warnIfMissing && !currentDetails && !hasDefault ? (
      <div className="mt-1.5 flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive">
        <WarningIcon className="size-3.5 shrink-0 mt-0.5" weight="fill" />
        <div>
          <T context="payment supplier bank warning">
            No bank selected and the supplier has no default payment details —
            this payment cannot be processed.
          </T>{' '}
          {supplier?.companyId && (
            <Link
              to="/companies/$id/supplier-info"
              params={{ id: supplier.companyId }}
              className="font-medium underline underline-offset-2"
            >
              <T context="payment supplier bank warning link">
                Add payment details
              </T>
            </Link>
          )}
        </div>
      </div>
    ) : null;

  if (!editable) {
    return (
      <div>
        <p className="text-sm font-medium">
          {currentDetails
            ? formatBankLabel(currentDetails)
            : currentDetailsId
              ? // The stored record was since deleted. Unapproved payments fall
                // back to the supplier's default details at approval.
                gt('Bank details no longer on file')
              : gt('Default')}
        </p>
        {missingBankWarning}
      </div>
    );
  }

  if (paymentDetails.length === 0) {
    return (
      <div>
        <p className="text-sm text-muted-foreground">
          <T context="ap supplier bank">No payment details on file</T>
        </p>
        {missingBankWarning}
      </div>
    );
  }

  function handleChange(value: string) {
    const detailsId = value === AUTO_VALUE ? null : value;
    if (detailsId === (currentDetailsId ?? null)) return;
    updatePayment.mutate({
      id: paymentId,
      data: { supplierPaymentDetailsId: detailsId },
    });
  }

  // A stored ID whose record was deleted falls back to Auto in the UI — the
  // server applies the same default-resolution at approval.
  const selectValue = currentDetails ? currentDetails.id : AUTO_VALUE;

  return (
    <div>
      <Select
        value={selectValue}
        onValueChange={handleChange}
        disabled={updatePayment.isPending}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={AUTO_VALUE}>
            <T context="ap supplier bank">Auto (Default)</T>
          </SelectItem>
          {paymentDetails.map((details) => (
            <SelectItem key={details.id} value={details.id}>
              <span className="flex items-center gap-2">
                {formatBankLabel(details)}
                {details.isDefault && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                    <T context="supplier bank default badge">Default</T>
                  </Badge>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {missingBankWarning}
    </div>
  );
}
